import type { DailyReportResponse } from 'api/generated'
import {
  axisProps,
  formatPeriod,
  formatRowDate,
  type Granularity,
} from 'helpers/daily-report'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { MetricTile } from './MetricTile'

const SCHEDULED_COLOR = 'var(--chart-2)'
const CONFIRMED_COLOR = 'var(--chart-1)'
const LOST_COLOR = 'var(--chart-5)'
const CANCELLED_COLOR = 'var(--chart-3)'

const integer = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 })
const pct = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 })
const n = (value: number | null | undefined) =>
  value == null ? '–' : integer.format(value)
const p = (value: number | null | undefined) =>
  value == null ? '–' : `${pct.format(value)}%`

type Point = {
  date: string
  scheduled: number | null
  confirmed: number | null
  unsuccessful: number | null
  cancelled: number | null
}

const sumOf = (...values: (number | null | undefined)[]) =>
  values.every((v) => v == null)
    ? null
    : values.reduce<number>((total, v) => total + (v ?? 0), 0)

const PointTooltip = ({
  active,
  payload,
  granularity,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
  granularity: Granularity
}) => {
  const point = payload?.[0]?.payload as Point | undefined
  if (!active || !point) return null
  return (
    <div className='rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm'>
      <div className='text-muted-foreground'>
        {formatRowDate(point.date, granularity)}
      </div>
      <div className='tabular-nums'>
        Programmati il giorno prima: <b>{n(point.scheduled)}</b>
      </div>
      <div className='tabular-nums'>
        Confermati: <b>{n(point.confirmed)}</b>
      </div>
      <div className='tabular-nums'>
        Falliti o saltati: <b>{n(point.unsuccessful)}</b>
      </div>
      <div className='tabular-nums'>
        Cancellazioni Recharge: <b>{n(point.cancelled)}</b>
      </div>
    </div>
  )
}

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className='inline-flex items-center gap-1'>
    <span className='h-2 w-2 rounded-sm' style={{ background: color }} />
    {label}
  </span>
)

/**
 * Churn of the period: Recharge cancellations over the active subscriptions, and the
 * renewals scheduled the day before compared with the confirmed ones (`renewals` source).
 */
export const RenewalOutcomes = ({
  renewals,
  recharge,
}: {
  renewals?: DailyReportResponse
  recharge?: DailyReportResponse
}) => {
  const granularity = (renewals ?? recharge)?.period.granularity ?? 'day'
  const unitPlural = granularity === 'day' ? 'giorni' : 'settimane'

  // Churn rate: cancelled in the period over the active subscriptions at its start
  // (the last value of the previous period, else the first day with data)
  const cancelled = recharge?.summary.cancelled_subscriptions
  const activeAtStart =
    recharge?.previous_summary.active_subscriptions ??
    recharge?.rows[0]?.values.active_subscriptions
  const days = recharge?.period.days ?? 0
  const churnRate =
    cancelled != null && activeAtStart
      ? (cancelled / activeAtStart) * 100
      : null
  const monthlyChurn =
    churnRate != null && days > 0 ? (churnRate * 30) / days : null

  // Renewal churn: only the rows with both the snapshot and the outcome
  const compared = (renewals?.rows ?? []).filter(
    (row) =>
      row.values.scheduled_charges != null &&
      row.values.confirmed_charges != null
  )
  const scheduled = sumOf(...compared.map((r) => r.values.scheduled_charges))
  const confirmed = sumOf(...compared.map((r) => r.values.confirmed_charges))
  const lost =
    scheduled != null && confirmed != null
      ? Math.max(scheduled - confirmed, 0)
      : null
  const lostPct = lost != null && scheduled ? (lost / scheduled) * 100 : null
  const failed = renewals?.summary.failed_charges
  const skipped = renewals?.summary.skipped_charges

  const cancelledByDate = new Map(
    (recharge?.rows ?? []).map((r) => [
      r.date,
      r.values.cancelled_subscriptions,
    ])
  )
  const dates = [
    ...new Set([
      ...(renewals?.rows ?? []).map((r) => r.date),
      ...cancelledByDate.keys(),
    ]),
  ].sort()
  const renewalsByDate = new Map(
    (renewals?.rows ?? []).map((r) => [r.date, r.values])
  )
  const points: Point[] = dates.map((date) => {
    const values = renewalsByDate.get(date)
    return {
      date,
      scheduled: values?.scheduled_charges ?? null,
      confirmed: values?.confirmed_charges ?? null,
      unsuccessful: sumOf(values?.failed_charges, values?.skipped_charges),
      cancelled: cancelledByDate.get(date) ?? null,
    }
  })

  return (
    <div className='rounded-lg border p-4'>
      <h3 className='text-sm font-semibold'>Churn e rinnovi confermati</h3>
      <p className='text-xs text-muted-foreground'>
        Le cancellazioni su Recharge arrivano in qualsiasi giorno (si può
        disattivare il rinnovo anche mesi prima), quindi non coincidono con il
        giorno del rinnovo: il churn al rinnovo è la differenza tra i rinnovi
        programmati il giorno prima e quelli confermati
        {recharge &&
          `, ${formatPeriod(recharge.period.from, recharge.period.to)}`}
        .
      </p>

      <div className='mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6'>
        <MetricTile
          label='Churn rate'
          value={p(churnRate)}
          note={`${n(cancelled)} cancellati su ${n(activeAtStart)} attivi`}
          title='Abbonamenti cancellati nel periodo diviso gli abbonamenti attivi a inizio periodo (Recharge)'
        />
        <MetricTile
          label='Churn mensile'
          value={p(monthlyChurn)}
          note='rapportato a 30 giorni'
        />
        <MetricTile
          label='Rinnovi programmati'
          value={n(scheduled)}
          note={`${compared.length} ${unitPlural} con esito`}
          title='Addebiti in coda su Recharge contati il giorno prima del rinnovo: raccolti solo da quando esiste il job'
        />
        <MetricTile
          label='Rinnovi confermati'
          value={n(confirmed)}
          note={
            confirmed != null && scheduled
              ? `${p((confirmed / scheduled) * 100)} dei programmati`
              : undefined
          }
        />
        <MetricTile
          label='Rinnovi persi'
          value={n(lost)}
          note={lostPct != null ? `${p(lostPct)} churn al rinnovo` : undefined}
          title='Programmati meno confermati: cancellati prima del giorno, pagamento fallito o saltati'
        />
        <MetricTile
          label='Falliti / saltati'
          value={`${n(failed)} / ${n(skipped)}`}
          note='nel periodo, per giorno di rinnovo'
          title='I pagamenti falliti vengono ritentati da Recharge nei giorni dopo'
        />
      </div>

      {points.length > 0 && (
        <>
          <div className='mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground'>
            <LegendDot color={SCHEDULED_COLOR} label='Programmati' />
            <LegendDot color={CONFIRMED_COLOR} label='Confermati' />
            <LegendDot color={LOST_COLOR} label='Falliti o saltati' />
            <LegendDot color={CANCELLED_COLOR} label='Cancellazioni Recharge' />
          </div>
          <div className='mt-2 h-[220px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <ComposedChart
                data={points}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                barGap={2}
              >
                <CartesianGrid
                  vertical={false}
                  stroke='var(--border)'
                  strokeDasharray='3 3'
                />
                <XAxis
                  dataKey='date'
                  tickFormatter={(d: string) =>
                    formatRowDate(d, granularity).replace('Sett. ', '')
                  }
                  minTickGap={24}
                  {...axisProps}
                />
                <YAxis
                  allowDecimals={false}
                  width={48}
                  domain={[0, 'auto']}
                  {...axisProps}
                />
                <Tooltip
                  cursor={{ fill: 'var(--muted)' }}
                  content={({ active, payload }) => (
                    <PointTooltip
                      active={active}
                      payload={payload}
                      granularity={granularity}
                    />
                  )}
                />
                {(
                  [
                    ['scheduled', SCHEDULED_COLOR],
                    ['confirmed', CONFIRMED_COLOR],
                    ['unsuccessful', LOST_COLOR],
                  ] as const
                ).map(([key, color]) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    fill={color}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={16}
                    isAnimationActive={false}
                  />
                ))}
                <Line
                  type='monotone'
                  dataKey='cancelled'
                  stroke={CANCELLED_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
      <p className='mt-2 text-xs text-muted-foreground'>
        I programmati sono raccolti ogni notte per il giorno dopo (non esiste
        uno storico precedente); l&apos;esito arriva due giorni dopo il rinnovo.
      </p>
    </div>
  )
}
